import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { tenantService } from "../services/tenantService";
import { tenantDisplayId } from "./TenantCommonStore";
import { Tenant } from "../../../api/graphql";
import { tenantTypeDisplayId } from "../../tenantType/store/TenantTypeCommonStore";
import { tenantStatusDisplayId } from "../../tenantStatus/store/TenantStatusCommonStore";
import { tenantPlanDisplayId } from "../../tenantPlan/store/TenantPlanCommonStore";
import { llmProviderDisplayId } from "../../llmProvider/store/LlmProviderCommonStore";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { EsignatureProvider } from "../../../api/graphql";
import { tenantTypeService } from "../../tenantType/services/tenantTypeService";
import { tenantStatusService } from "../../tenantStatus/services/tenantStatusService";
import { tenantPlanService } from "../../tenantPlan/services/tenantPlanService";
import { llmProviderService } from "../../llmProvider/services/llmProviderService";
import { InitializeParams } from "../../../store/types";

export class TenantViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = tenantService;
  tenantInstance: Tenant | null = null;
  cardState: Record<string, boolean> = {
    tenant: true,
    llmLimits: true,
    storageLimits: true,
  };
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  nameValue: string = "";
  tenantTypeService = tenantTypeService;
  tenantTypeSearchValue: string = "";
  tenantTypeIdValue: string = "";
  tenantTypeSelectedValue: { displayId: string; id: string } | null = null;
  statusService = tenantStatusService;
  statusSearchValue: string = "";
  statusIdValue: string = "";
  statusSelectedValue: { displayId: string; id: string } | null = null;
  planService = tenantPlanService;
  planSearchValue: string = "";
  planIdValue: string = "";
  planSelectedValue: { displayId: string; id: string } | null = null;
  logoFiles: CrudRows = {};
  publicUrlValue: string = "";
  idERPValue: string = "";
  idCRMValue: string = "";
  monthlyTokenLimitValue: number | undefined = undefined;
  currentMonthTokensValue: number = 0;
  tokenLimitResetDayValue: number = 0;
  lastTokenResetDateValue: Date | null = null;
  ragLlmProviderService = llmProviderService;
  ragLlmProviderSearchValue: string = "";
  ragLlmProviderIdValue: string = "";
  ragLlmProviderSelectedValue: { displayId: string; id: string } | null = null;
  ragLlmModelValue: string = "";
  currentStorageBytesValue: number = 0;
  storageByteLimitValue: number | undefined = undefined;
  eSignatureProviderValue: EsignatureProvider | "" = "" as
    | EsignatureProvider
    | "";
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadTenantById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.tenantOne({ id });
    this.tenantInstance = result.tenantOne;
    if (this.tenantInstance) {
      this.loadValues(this.tenantInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.tenantInstance) {
      return tenantDisplayId(this.tenantInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.TenantSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "tenantType") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.tenantTypeOne({
          id: params.returnData.resultId,
        });
        const tenantType = result.tenantTypeOne;
        this.tenantTypeSelectedValue = tenantType;
      } else if (params.callerData?.fieldName === "status") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.tenantStatusOne({
          id: params.returnData.resultId,
        });
        const tenantStatus = result.tenantStatusOne;
        this.statusSelectedValue = tenantStatus;
      } else if (params.callerData?.fieldName === "plan") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.tenantPlanOne({
          id: params.returnData.resultId,
        });
        const tenantPlan = result.tenantPlanOne;
        this.planSelectedValue = tenantPlan;
      } else if (params.callerData?.fieldName === "ragLlmProvider") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.llmProviderOne({
          id: params.returnData.resultId,
        });
        const llmProvider = result.llmProviderOne;
        this.ragLlmProviderSelectedValue = llmProvider;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get tenantTypeValue() {
    if (this.tenantTypeSelectedValue) {
      return {
        id: this.tenantTypeSelectedValue.id,
        displayId: tenantTypeDisplayId(
          this.tenantTypeSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get statusValue() {
    if (this.statusSelectedValue) {
      return {
        id: this.statusSelectedValue.id,
        displayId: tenantStatusDisplayId(
          this.statusSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get planValue() {
    if (this.planSelectedValue) {
      return {
        id: this.planSelectedValue.id,
        displayId: tenantPlanDisplayId(this.planSelectedValue, this.langStore),
      };
    }
    return null;
  }

  get ragLlmProviderValue() {
    if (this.ragLlmProviderSelectedValue) {
      return {
        id: this.ragLlmProviderSelectedValue.id,
        displayId: llmProviderDisplayId(
          this.ragLlmProviderSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  getESignatureProviderOptions() {
    return this.langStore.EsignatureProviderOptions;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "Tenant",
      fieldName: fieldName,
      instanceId: this.tenantInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "Tenant",
        fieldName: fieldName,
        instanceId: this.tenantInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.tenantInstance = null;
    this.nameValue = "";
    this.tenantTypeSearchValue = "";
    this.tenantTypeIdValue = "";
    this.statusSearchValue = "";
    this.statusIdValue = "";
    this.planSearchValue = "";
    this.planIdValue = "";
    this.logoFiles = {};
    this.publicUrlValue = "";
    this.idERPValue = "";
    this.idCRMValue = "";
    this.monthlyTokenLimitValue = undefined;
    this.currentMonthTokensValue = 0;
    this.tokenLimitResetDayValue = 0;
    this.lastTokenResetDateValue = null;
    this.ragLlmProviderSearchValue = "";
    this.ragLlmProviderIdValue = "";
    this.ragLlmModelValue = "";
    this.currentStorageBytesValue = 0;
    this.storageByteLimitValue = undefined;
    this.eSignatureProviderValue = "" as EsignatureProvider | "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelTenant() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("tenant_update");
  }

  editTenant() {
    const id = this.tenantInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/tenant/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printTenant(handlePrint: () => void): void {
    handlePrint();
  }

  *closeTenant(): any {
    yield this.close();
  }

  loadValues(instance: Tenant) {
    this.idValue = instance.id;
    this.nameValue = instance.name;
    this.tenantTypeSelectedValue = instance.tenantType
      ? instance.tenantType
      : null;
    this.statusSelectedValue = instance.status ? instance.status : null;
    this.planSelectedValue = instance.plan ? instance.plan : null;
    this.logoFiles = instance.logo_data
      ? {
          [instance.logo_data.name]: {
            name: instance.logo_data.name,
            formattedSize: formatBytes(instance.logo_data?.size || 0),
            mimetype: instance.logo_data?.mimetype,
            id: instance.logo_data.name,
            __status__: "original",
          },
        }
      : {};
    this.publicUrlValue = instance.publicUrl || "";
    this.idERPValue = instance.idERP || "";
    this.idCRMValue = instance.idCRM || "";
    this.monthlyTokenLimitValue = instance.monthlyTokenLimit || undefined;
    this.currentMonthTokensValue = instance.currentMonthTokens;
    this.tokenLimitResetDayValue = instance.tokenLimitResetDay;
    this.lastTokenResetDateValue = instance.lastTokenResetDate
      ? new Date(instance.lastTokenResetDate)
      : null;
    this.ragLlmProviderSelectedValue = instance.ragLlmProvider
      ? instance.ragLlmProvider
      : null;
    this.ragLlmModelValue = instance.ragLlmModel || "";
    this.currentStorageBytesValue = instance.currentStorageBytes;
    this.storageByteLimitValue = instance.storageByteLimit || undefined;
    this.eSignatureProviderValue =
      instance.eSignatureProvider || ("" as EsignatureProvider | "");
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadTenantById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
